import type { Html, PhrasingContent, Text } from 'mdast';
import type { Node, Parent } from 'unist';

export type TextReferenceMatch = {
  end: number;
  start: number;
};

/** Splits `content` into text nodes and a link node for each of `references`, in order. */
export function replaceTextReferences<M extends TextReferenceMatch, T>(
  content: string,
  references: M[],
  createTextNode: (value: string) => T,
  createLinkNode: (match: M) => T,
): T[] | null {
  if (references.length === 0) {
    return null;
  }

  const replacements: T[] = [];
  let cursor = 0;

  for (const match of references) {
    if (match.start > cursor) {
      replacements.push(createTextNode(content.slice(cursor, match.start)));
    }

    replacements.push(createLinkNode(match));
    cursor = match.end;
  }

  if (cursor < content.length) {
    replacements.push(createTextNode(content.slice(cursor)));
  }

  return replacements;
}

function hasChildren(node: Node): node is Parent {
  return 'children' in node && Array.isArray(node.children);
}

function updateRawAnchorDepth(currentDepth: number, html: string): number {
  let nextDepth = currentDepth;
  // Tags inside an HTML comment are not rendered, so they must not open or close an anchor.
  const htmlWithoutComments = html.replace(/<!--[\s\S]*?(?:-->|$)/g, '');

  for (const match of htmlWithoutComments.matchAll(/<\/?a(?:\s[^>]*)?>/gi)) {
    const tag = match[0];
    nextDepth = tag.startsWith('</') ? Math.max(0, nextDepth - 1) : nextDepth + 1;
  }

  return nextDepth;
}

/**
 * Replaces the markdown text nodes that `linkify` returns nodes for, skipping text inside links and
 * raw HTML anchors. Code never reaches `linkify`, because its value is not a text node.
 */
export function linkifyMarkdownText(
  parent: Parent,
  linkify: (node: Text) => PhrasingContent[] | null,
): void {
  let rawAnchorDepth = 0;

  for (let index = 0; index < parent.children.length; index += 1) {
    const child = parent.children[index];
    if (!child || child.type === 'link' || child.type === 'linkReference') {
      continue;
    }

    if (child.type === 'html') {
      rawAnchorDepth = updateRawAnchorDepth(rawAnchorDepth, (child as Html).value);
      continue;
    }

    if (rawAnchorDepth > 0) {
      continue;
    }

    if (child.type === 'text') {
      const replacements = linkify(child as Text);
      if (replacements) {
        parent.children.splice(index, 1, ...replacements);
        index += replacements.length - 1;
      }
      continue;
    }

    if (hasChildren(child)) {
      linkifyMarkdownText(child, linkify);
    }
  }
}
